import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { completeLogin, HARNESS_OPENER_ORIGIN_KEY } from './authCodeFlow';
import { useAuth } from './AuthContext';
import { useTranslation } from '@src/i18n';
import { hostBridge } from '@src/messaging/hostBridge';
import { LoadingPage } from '@src/components/LoadingPage';
import { isOriginAllowed } from '@src/messaging/originAllowlist';

/**
 * Handles the OAuth redirect back from FusionAuth: validates state, exchanges
 * the authorization code for tokens, stores them in AuthContext, and sends the
 * user to wherever they originally tried to go (or "/" by default).
 *
 * React 18 StrictMode double-invokes effects in development; we guard against
 * running the exchange twice because authorization codes are single-use.
 */
export function CallbackPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { setTokens } = useAuth();
    const { t } = useTranslation();
    const [error, setError] = useState<string | null>(null);
    const hasRunRef = useRef(false);

    useEffect(() => {
        // StrictMode double-invokes effects; hasRunRef guards against running
        // the exchange twice (authorization codes are single-use). We can't
        // also use a `cancelled` cleanup flag here — under StrictMode the
        // first mount's cleanup would cancel its own in-flight exchange while
        // the second mount skips fetching entirely, silently dropping tokens.
        if (hasRunRef.current) return;
        hasRunRef.current = true;

        (async () => {
            try {
                // Read harness opener origin before completeLogin clears sessionStorage.
                const openerOrigin = sessionStorage.getItem(
                    HARNESS_OPENER_ORIGIN_KEY
                );

                const { tokens, redirectTo } = await completeLogin(
                    searchParams
                );
                setTokens({
                    accessToken: tokens.accessToken,
                    refreshToken: tokens.refreshToken,
                    expiresAt: new Date(
                        Date.now() + tokens.expiresIn * 1000
                    ).toISOString(),
                    mode: 'browser'
                });

                if (
                    openerOrigin &&
                    isOriginAllowed(openerOrigin) &&
                    window.opener &&
                    tokens.refreshToken
                ) {
                    window.opener.postMessage(
                        {
                            type: '7punches:harness_token',
                            payload: { refreshToken: tokens.refreshToken }
                        },
                        openerOrigin
                    );
                    window.close();
                    return;
                }

                if (tokens.refreshToken) {
                    hostBridge.sessionToken({
                        refreshToken: tokens.refreshToken
                    });
                }
                navigate(redirectTo, { replace: true });
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : t('callback.failed')
                );
            }
        })();
    }, [searchParams, setTokens, navigate, t]);

    if (error) {
        return (
            <main className="callback-page">
                <h1>{t('callback.failed')}</h1>
                <p role="alert">{error}</p>
                <button
                    type="button"
                    onClick={() => navigate('/login', { replace: true })}
                >
                    {t('callback.tryAgain')}
                </button>
            </main>
        );
    }

    return <LoadingPage />;
}
